import type { FC } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

import { noDataAvailableStyles } from 'src/theme/sharedStyles';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import Pill from 'src/components/followUp/common/Pill/Pill';

export interface StackedBarWithLabelProps {
  stackLabels: Array<{
    name: string;
    color: string;
    borderColor: string;
    comparisonColor?: string;
    comparisonBorderColor?: string;
  }>;
  label?: string;
  total: number;
  stacks: Array<{ stackLabel: string; value: number }>;
  highestTotal: number;
  height: string;
  comparison?: boolean;
}

const StackedBarWithLabel: FC<StackedBarWithLabelProps> = ({
  stackLabels,
  label,
  total,
  stacks,
  highestTotal,
  height,
  comparison = false,
}) => (
  <Box
    key={label}
    sx={{
      width: '100%',
      display: 'flex',
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: '240px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label && (
        <Tooltip title={label}>
          <Typography
            variant="body3"
            sx={{
              whiteSpace: 'nowrap',
              fontWeight: 600,
            }}
          >
            {label}
          </Typography>
        </Tooltip>
      )}
      <Pill
        label={total.toLocaleString()}
        color={comparison ? 'comparisonPrimary' : 'primary'}
        sx={{
          padding: '2px 8px',
        }}
      />
    </Box>
    <Box
      sx={{
        display: 'flex',
        height: height,
        flexGrow: 1,
      }}
    >
      {total <= 0 ? (
        <Box sx={noDataAvailableStyles.noDataAvailable} />
      ) : (
        stacks.map((stack) => {
          if (stack.value === 0) return null;
          const { color, borderColor, comparisonColor, comparisonBorderColor } =
            stackLabels.find((label) => label.name === stack.stackLabel) ?? {
              color: undefined,
              borderColor: undefined,
              comparisonColor: undefined,
              comparisonBorderColor: undefined,
            };
          return (
            <Box
              key={stack.stackLabel}
              sx={[
                {
                  width: `${getRelativeValue(stack.value, highestTotal)}%`,
                  minWidth: '5%',
                  bgcolor: comparison ? comparisonColor : color,
                },
                {
                  'display': 'flex',
                  'justifyContent': 'center',
                  'alignItems': 'center',
                  'borderRight': '2px solid',
                  '&:last-child': {
                    borderRight: 'none',
                  },
                },
                comparison
                  ? {
                      borderColor: comparisonBorderColor,
                    }
                  : {
                      borderColor: borderColor,
                    },
              ]}
            >
              <Pill
                label={stack.value.toLocaleString()}
                color={comparison ? 'comparisonSecondary' : 'lightPrimary'}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          );
        })
      )}
    </Box>
  </Box>
);

export default StackedBarWithLabel;
